/* eslint-disable no-unused-vars */
import axios from "axios";
import { OFFERS_API, BFF_API, BFF_MALL_API } from "./../../../js/shared/constants";
import {
	getUserToken,
	makeDownload,
	formatPricesQuickSale,
	formatPricesForwardPayment
} from "./utils";

const withPXCookie = function(config) {
	const cookie = localStorage.getItem("pxcookie");
	if (cookie) {
		config.headers["x-px-cookies"] = cookie;
	}
	return config;
};

export const offersApi = axios.create({
	baseURL: OFFERS_API
});

offersApi.interceptors.request.use(withPXCookie);

const defaultToken = getUserToken();
/** FIDELITY PROGRAMA DE FIDELIDADE */

const BILLING_URL = `${BFF_MALL_API}/miles`;

/**
 * Dados de login referente a um programa de fidelidade
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId
 * @param {String} username
 * @param {String} password
 */
export const sendCreateUpdateFidelityLogin = (token = defaultToken, data) => {
	return offersApi.put("/fidelity/login", data, { headers: { Authorization: token } });
};

/**
 * Recupera todos programas de fidelidades por tipo (nacional ou internacional)
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {(1|2)} type - 1 - Nacional | 2 - Internacional
 *
 * TODO: Implementado para ignorar Latam
 */
export const getFidelities = (token = defaultToken, type = false) => {
	const filterType = type !== false ? `?type=${type}` : "";
	return offersApi(`/fidelity${filterType}`, {}).then(result => {
		result.data = result.data.filter(airline => {
			airline.fidelities = airline.fidelities.filter(fid => fid.id != 1);
			return airline;
		});
		return result;
	});
};

/**
 * Recupera os programas de fidelidades que o usuario já realizou vendas
 */
export const getFidelitiesFromSales = () =>
	offersApi.get("/user/fidelity-sales", {
		headers: { Authorization: getUserToken() }
	});

/**
 * Recupera os dados de login de acordo com o programa de fidelidade
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId ID do Programa de Fidelidade
 */
export const getFidelityLogin = (token = defaultToken, fidelityId) =>
	offersApi.get(`/offer/fidelity-data/${fidelityId}`, {
		headers: { Authorization: token }
	});

/** OFFER - OFERTA DE MILHAS */

/**
 * Retorna todas as ofertas feitas pelo usuário
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getOffers = (token = defaultToken, fidelityId = null) => {
	let fidelity = "";

	if (fidelityId) fidelity = `?fidelityId=${fidelityId}`;

	return offersApi.get(`/offer${fidelity}`, {
		headers: { authorization: getUserToken() }
	});
};

/**
 * Retorna o status de acordo com a proposta de venda
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId Id do programa de fidelidade
 * @param {Number} price Preço da milhagem
 * @param {Number} miles Quantidade de milhas
 */
export const getOfferState = (token = defaultToken, fidelityId, price, miles) =>
	offersApi.get(`/offer/state/${fidelityId}?price=${price}&miles=${miles}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Retorna as estatisticas da media de milhas
 */
export const getMilesStatistics = fidelityId =>
	offersApi.get(`/offer/sales-statistics/${fidelityId}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Recupera todos os ofertantes de um programa de fidelidade ordenados pelo rank
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId Id do programa de fidelidade
 * @param {Number} quantMiles Quantidade de milhas para filtrar o rank
 * @param {Number} offset Quantidade de elementos a serem enviados, 0 retorna todos do rank paginados
 * @param {Number} page Pagina do elemento caso exista paginação
 * @param {Number} maxElements Maximo de elementos por pagina
 */
export const getRanksByFidelityAndMiles = (
	token = defaultToken,
	fidelityId,
	quantMiles,
	offset,
	page,
	maxElements
) =>
	offersApi.get(`/ranking/fidelity/${fidelityId}/${quantMiles}?offset=${offset}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Recupera o rank de acordo com o id de oferta
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} offerId Id da oferta
 */
export const getRankByOfferId = (token = defaultToken, offerId) =>
	offersApi.get(`/ranking/offer/${offerId}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Recupera os preços para cada faixa de milhas
 * @param {Number} fidelityId Id do programa de fidelidade
 * @param {Number} quantMiles Quantidade de milhas para filtrar o rank
 * @param {Number} averageMiles Opcional, média de milhas filtrar faixas de preço
 */
export const getPricesByFidelityAndMiles = (fidelityId, quantMiles, averageMiles) =>
	axios.get(
		`${BFF_API}/offer/prices/${fidelityId}/${quantMiles}${
			averageMiles ? `?average=${averageMiles}` : ""
		}`
	);

/**
 * Recupera os preços e todos os ofertantes de um programa de fidelidade ordenados pelo rank
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId Id do programa de fidelidade
 * @param {Number} quantMiles Quantidade de milhas para filtrar o rank
 * @param {Number} offset Quantidade de elementos a serem enviados, 0 retorna todos do rank paginados
 */
export const getRankPriceByFidelityAndMiles = (
	token = defaultToken,
	fidelityId,
	quantMiles,
	offset,
	paymentDeadline,
	paymentScheduleType
) =>
	offersApi.get(
		`/ranking/fidelity/${fidelityId}/${quantMiles}/prices?offset=${offset}
		${paymentDeadline ? `&?paymentDeadline=${paymentDeadline}` : ""}
		${paymentScheduleType ? `&?paymentScheduleType=${paymentScheduleType}` : ""}`,
		{
			headers: { Authorization: token }
		}
	);

/**
 * Retorna os possíveis motivos para excluir uma oferta
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getCancelationReasons = (token = defaultToken) =>
	offersApi.get("/offer/cancellation-reasons", {
		headers: { authorization: getUserToken() }
	});

/**
 * Retorna o valor médio das ofertas do planos de fidelidade nacionais
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getMarketAnalysis = (token = defaultToken, id = null) =>
	offersApi.get(`/offer/market-analysis/${id ? id : ""}`, {
		headers: { authorization: getUserToken() }
	});

/**
 * Retorna o valor fixo pra venda rápida
 */
export const getPricesQuickSale = () =>
	axios.get(`${BFF_API}/offer/pricing/quicksale`).then(result => {
		return formatPricesQuickSale(result);
	});

/**
 * Retorna o valor fixo para pagamento antecipado
 */
export const getPricesForwardPayment = () =>
	axios.get(`${BFF_API}/offer/pricing/forward-payment`).then(result => {
		return formatPricesForwardPayment(result);
	});

/**
 * Cria uma oferta
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId Id do programa de fidelidade
 * @param {Number} quant
 * @param {Number} price
 * @param {String} login
 * @param {String} password
 * @param {String} phone
 * @param {Number} averageMiles
 * @param {String} pwAwardSecondary
 */
export const createOffer = (
	token = defaultToken,
	fidelityId,
	quant,
	price,
	login,
	password,
	phone,
	averageMiles,
	pwAwardSecondary,
	reductions,
	quickSale
) => {
	return offersApi.post(
		`/offer/${fidelityId}`,
		{
			id: fidelityId,
			quant: quant,
			price: price,
			login: login,
			password: password,
			phone: phone,
			averageMiles: averageMiles,
			pwAwardSecondary: pwAwardSecondary,
			reductions,
			quickSale: quickSale ? 1 : undefined
		},
		{ headers: { Authorization: token } }
	);
};

/**
 * Executa um pedido de exportação por um periodo e envia por email
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {String} email E-mail que receberá a exportação
 * @param {Number} start Data inicial
 * @param {Number} end Data final
 * @param {Boolean} toreceive True or false se deseja exportar vendas a receber
 * @param {Boolean} received True or false se deseja exportar vendas recebidas
 * @param {Boolean} cancel True or false se deseja exportar vendas canceladas
 */
export const exportOffer = filteredStatements => {
	return offersApi.put("/offer/export", filteredStatements, {
		headers: { Authorization: getUserToken() }
	});
};

/**
 * Executa um pedido de exportação por um periodo e faz o download
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} start Data inicial
 * @param {Number} end Data final
 * @param {Boolean} toreceive True or false se deseja exportar vendas a receber
 * @param {Boolean} received True or false se deseja exportar vendas recebidas
 * @param {Boolean} cancel True or false se deseja exportar vendas canceladas
 */
export const exportToDownloadOffer = filteredStatements => {
	return (
		offersApi
			.put("/offer/export-download", filteredStatements, {
				headers: { Authorization: getUserToken() }
			})
			// eslint-disable-next-line promise/always-return
			.then(res => {
				makeDownload(res.data, `minhas-vendas-maxmilhas.${filteredStatements.fileType}`);
			})
	);
};

/**
 * Executa um pedido de exportação do ano para o IRPF e faz o download
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const exportIRPFToDownloadOffer = (type, year) => {
	return offersApi
		.get(`/offer/irpf-export-download?type=${type}&year=${year}`, {
			headers: { Authorization: defaultToken }
		})
		.then(res => {
			makeDownload(res.data, `declaracao${year}_MaxMilhas.${type}`);
		})
		.catch(err => {
			throw err.response.data;
		});
};

export const getIRPFYears = () =>
	offersApi.get("/offer/irpf-sales", { headers: { Authorization: defaultToken } });

/**
 * Atualiza uma oferta
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} id Id da oferta
 * @param {Number} quant
 * @param {Number} price
 * @param {String} password
 * @param {String} phone
 * @param {Number} averageMiles
 * @param {String} pwAwardSecondary
 */
export const updateOffer = (
	token = defaultToken,
	id,
	fidelityId,
	quant,
	price,
	login,
	password,
	phone,
	averageMiles,
	pwAwardSecondary,
	reductions,
	quickSale
) => {
	return offersApi.put(
		`/offer/${id}`,
		{
			fidelityId: fidelityId,
			quant: quant,
			price: price,
			login: login,
			password: password,
			phone: phone,
			averageMiles: averageMiles,
			pwAwardSecondary: pwAwardSecondary,
			reductions,
			quickSale: quickSale ? 1 : undefined
		},
		{ headers: { Authorization: token } }
	);
};

/**
 * Troca rápida de preço
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} id Id da oferta
 * @param {Number} price Novo preço para oferta
 */
export const quickChangePrice = (token = defaultToken, id, price) => {
	return offersApi.patch(
		`/offer/price/${id}`,
		{
			price: price
		},
		{ headers: { Authorization: token } }
	);
};

/**
 * Deleta uma oferta
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} id Id da oferta
 * @param {String} reason Motivo para deletar a oferta
 * @param {String} comment Comentário do usuário sobre o porque está deletando a oferta
 */
export const deleteOffer = (token = defaultToken, id, reason, comment) => {
	return offersApi.delete(`/offer/${id}`, {
		headers: { Authorization: token },
		data: { reason, comment }
	});
};

/**
 * Deleta o cartão de credito
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const deleteUserCreditCard = (token = defaultToken) => {
	return offersApi.delete("/user/creditcard", { headers: { Authorization: token } });
};

/**
 * Deleta o cartão de credito
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const deleteUserBank = (token = defaultToken) => {
	return offersApi.delete("/user/bank", { headers: { Authorization: token } });
};

/**
 * Deleta os dados de login do programa de fidelidade
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} fidelityId Id do programa de fidelidade
 */
export const deleteFidelityLogin = fidelityId => {
	return offersApi.delete(`/fidelity/${fidelityId}`, {
		headers: { Authorization: getUserToken() }
	});
};

/** USER - OPERAÇÕES REFERENTES AO USUÁRIO */

/**
 * Retorna os pagamentos realizados no mes corrente
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getUserPayments = (token = defaultToken) =>
	offersApi.get("/user/payments", {
		headers: { Authorization: getUserToken() }
	});

/**
 * Retorna todas as vendas realizadas por um periodo e por tipo
 * @param {Object} filteredParameters (todos os atributos de filtro em um objeto)
 */
export const getUserSalesByPeriod = filteredParameters => {
	return offersApi.post("/user/list-sales", filteredParameters, {
		headers: { Authorization: getUserToken() }
	});
};

/**
 * Retorna uma venda por E-ticket
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {String} eTicket Retorna uma venda de acordo com o E-ticket
 */
export const getUserSaleByTicket = (token = defaultToken, eTicket) =>
	offersApi.get(`/user/sales/search?eTicket=${eTicket}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Retorna uma venda detalhada
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} id Id da venda
 */
export const getUserSaleDetailed = (token = defaultToken, id) =>
	offersApi.get(`/user/sales/${id}`, {
		headers: { Authorization: getUserToken() }
	});

/**
 * Retorna a lista dos bancos cadastrados
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getBanks = (token = defaultToken) =>
	offersApi.get("/user/banks", {
		headers: { Authorization: token }
	});

/**
 * Retorna os dados bancarios do usuário
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getUserBank = (token = defaultToken) =>
	offersApi.get("/user/bank", {
		headers: { Authorization: token }
	});

/**
 * Retorna o cartão de crédito do usuário
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getUserCreditCard = (token = defaultToken) =>
	offersApi.get("/user/creditcard", {
		headers: { Authorization: token }
	});

/**
 * Retorna se o usuário já possui as informações de banco cadastrado
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const hasBank = (token = defaultToken) =>
	offersApi.get("/user/hasbankdata", {
		headers: { Authorization: token }
	});

/**
 * Retorna se o usuário já possui as informações de cartão de crédito
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const hasCreditCard = (token = defaultToken) =>
	offersApi.get("/user/hascreditcard", {
		headers: { Authorization: token }
	});

/**
 * Adicionar/Atualizar a instituição financeira
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {Number} bankId
 * @param {String} agency
 * @param {String} type
 * @param {String} account
 * @param {String} cpfcnpj
 * @param {String} phone
 */
export const sendCreateUpdateBank = (
	token = defaultToken,
	bankId,
	agency,
	dacAgency,
	account,
	dacAccount,
	cpfcnpj
) => {
	return offersApi.post(
		"/user/bank",
		{
			bankId,
			agency,
			type: "c",
			dacAgency,
			account,
			dacAccount,
			cpfcnpj
		},
		{ headers: { Authorization: token } }
	);
};

/**
 * Adicionar/Atualizar um cartão de crédito
 * @param {String} token Token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {String} name
 * @param {Number} number
 * @param {Number} validDate
 * @param {Number} securityCode
 * @param {String} cpf
 */
export const sendCreateUpdateCreditCard = (
	token = defaultToken,
	name,
	number,
	validDate,
	securityCode,
	cpf
) => {
	return offersApi.post(
		"/user/creditcard",
		{
			name: name,
			number: number,
			validDate: validDate,
			securityCode: securityCode,
			cpf: cpf
		},
		{ headers: { Authorization: token } }
	);
};

/**
 * Abre um chamado relacionado à alteração do vôo
 * @param {Number} id
 */
export const saleFlightChangeNotify = id => {
	return offersApi.post(
		"/user/sale-flight-change-notify",
		{
			idSale: id
		},
		{ headers: { Authorization: getUserToken() } }
	);
};

/**
 * Abre um chamado relacionado à inconsistencia
 * @param {Object} data
 *	{
 *		"idSale": 0,
 *		"idTransaction": 0,
 * 		"inconsistency": "asdfsdaf"
 *	}
 */
export const saleInconsistency = data => {
	return offersApi.post("/user/sale-inconsistency", data, {
		headers: { Authorization: getUserToken() }
	});
};

/**
 * Consulta dados de vendas do usuário filtrando por data e programa de fidelidade
 * @param {String} token token de usuário logado (pode ser encontrado em localStorage - au)
 * @param {String} programId id do programa de fidelidade
 * @param {String} startDate data início para filtrar
 * @param {String} endDate data fim para filtrar
 */
export const getSalesReport = (token, programId, startDate, endDate) => {
	return offersApi.get(
		`/offer/passengers/${programId}?dateInit=${startDate}&dateFinish=${endDate}`,
		{
			headers: { Authorization: getUserToken() }
		}
	);
};

/**
 * Consulta se o usuário é gestor
 */
export const isManager = () => {
	return offersApi.get(`/user/is-manager`, {
		headers: { Authorization: getUserToken() }
	});
};

/**
 * Consulta todas os programas de fidelidade
 * @param {String} token token de usuário logado (pode ser encontrado em localStorage - au)
 */
export const getAllProgramFidelity = () => axios.get("/customer/my-sales/get-all-program-fidelity");

/**
 * Retorna o extrato de de débitos do usuário, revenda
 */
export const getExtractResale = username =>
	axios.get(`${BFF_API}/reseale-charge/extracts/${username}`, {
		headers: { Authorization: defaultToken }
	});

/**
 * Registra um log da ação do usuário, revenda
 * @param {Object} log
 */
export const postLogDecisionResale = log => {
	return axios.post(`${BFF_API}/reseale-charge/log-decision`, log, {
		headers: { Authorization: defaultToken }
	});
};

/**
 * Registra um pagamento via cartão de crédito ou transferência, revenda
 * @param {Object} payment
 */
export const postPaymentResale = payment => {
	return axios.post(`${BFF_API}/reseale-charge/payment`, payment, {
		headers: { Authorization: defaultToken }
	});
};

/**
 * Recupera dados de parcelas para pagamento via cartão de crédito, revenda
 * @param {String} username
 */
export const getInstallmentsResale = username =>
	axios.get(`${BFF_API}/reseale-charge/installments/${username}`, {
		headers: { Authorization: defaultToken }
	});

/**
 * Reporta problema informado pelo usuário, revenda
 * @param {Object} data
 */
export const postReportProblemResale = data =>
	axios.post(`${BFF_API}/reseale-charge/troubles`, data, {
		headers: { Authorization: defaultToken }
	});

/**
 * Envia uma requisição pra verificar se o token do usuário é válido
 * @param {String} token
 */
export const pingUser = token => axios.get("/is_granted", { headers: { authorization: token } });

export const logoutUser = () => axios.post("/logout");

/**
 * Autoriza a reutilização de milhas para revenda
 * @param {Object} data
 */
export const authorizeResale = data =>
	axios.post(`${BFF_API}/reseale-charge/authorize-resale`, data, {
		headers: { authorization: defaultToken }
	});

/**
 * Retorna as revendas disponíveis
 */
export const availableResale = data =>
	axios.get(`${BFF_API}/reseale-charge/available-resales`, {
		headers: { authorization: defaultToken }
	});

/**
 * Retorna o  de pendências financeiras do usuário
 */
export const getCustomerFinancialPending = userEmail =>
	axios.get(`${BILLING_URL}/customer/${userEmail}/billing`, {
		headers: { Authorization: defaultToken }
	});

/**
 * Cria e retorna a URL de pagamento das pendências financeiras selecionadas
 */
export const postCreatePaymentUrl = data =>
	axios.post(`${BILLING_URL}/billing-payment`, data, {
		headers: { authorization: defaultToken }
	});

/**
 * Altera o telefone dos dados de cadstro do programa de fidelidade
 * @param {Number} fidelityId (Programa de fidelidade)
 * @param {String} phone (Telefone)
 */
export const setFidelityPhoneByFidelityId = (fidelityId, phone) => {
	return axios.patch(
		`${BFF_API}/offer/fidelity/${fidelityId}/phone`,
		{ phone },
		{
			headers: { Authorization: defaultToken }
		}
	);
};

/**
 * Seta o tipo de autenticação de dois fatores do usuário para Redirecionamento de Email
 *
 * @param {string} token User auth token
 */
export const send2FAEmailRedirectOption = (token, params) =>
	axios.post(`${BFF_MALL_API}/miles/two-factor-send-email`, params, {
		headers: { authorization: token }
	});

/**
 * Retorna um bolean para determinar exibição das versões de um teste AB - Unleash
 * @param {String} featureFlag (Nome da feature flag)
 */
export const getUnleashEnabledFeature = featureFlag =>
	axios.get(`${BFF_MALL_API}/unleash/enabled/${featureFlag}`);

/**
 * Envia os dados necessários ao backend para iniciar o processo de autenticação de dois fatores (2FA) com a Latam.
 *
 * @param {string} password - Senha do usuário para acessar a conta.
 * @param {string} login - Nome de usuário ou identificador para login.
 * @param {string} sessionId - ID da sessão atual para indetificar a solicitação.
 * @param {string} methodTwoFactor - Método escolhido para receber o código 2FA (ex: "email").
 * @param {string} airlineProgram - Id do programa de fidelidade da companhia aérea (ex: "3").
 * @param {number} miles - Quantidade de milhas envolvidas na operação.
 * @param {number} price - Preço relacionado à oferta de milhas.
 *
 */
export const postRequest2FACodeLatam = ({
	password,
	login,
	sessionId,
	methodTwoFactor,
	airlineProgram,
	miles,
	price
}) => {
	const requestData = {
		password,
		login,
		sessionId,
		methodTwoFactor,
		airlineProgram,
		miles,
		price
	};
	return axios.post(`${BFF_MALL_API}/miles/analysis`, requestData, {
		headers: { Authorization: getUserToken() }
	});
};

/**
 * Envia o código de autenticação recebido pelo usuário ao backend.
 *
 * @param {string} code - Código de autenticação enviado ao usuário.
 * @param {string} sessionId - ID da sessão atual para indetificar a solicitação.
 * @param {string} airlineProgramId - ID do programa de fidelidade.
 *
 */
export const post2FACode = ({ code, sessionId, airlineProgramId }) => {
	const requestData = { code, sessionId, airlineProgramId };

	return offersApi.post("/offer/code", requestData, {
		headers: { Authorization: getUserToken() }
	});
};
